import { createLogger, environments, envs, urls } from '@sentry/shared';

/**
 * SECURITY (VULN-36931) — credential-emit-time origin validation.
 *
 * The legacy `<susi-sentry>` (packages/wrapper) embeds this app in an iframe and
 * passes `asserted_origin` (the host it wants the credential delivered to). That
 * value is attacker-controllable, so before the app emits a token/auth-code back
 * to the host we must confirm `asserted_origin` is a registered redirect URI for
 * the client.
 *
 * This runs at emit time — when a token exists — so it can use
 * `/ims/validate_client/v2`, which requires a bearer and is CORS-enabled for the
 * auth-light origin (the app-elm iframe uses the same endpoint the same way).
 * The old pre-connection check used `/ims/clients/validate/v1`, which is
 * bearer-gated + not CORS-enabled and therefore fails closed in the browser
 * (see VULN-36931 analysis); it could never work at connection-setup time
 * because no credential exists yet.
 *
 * Kept self-contained (only `@sentry/shared`) to avoid an import cycle with
 * `configurations-api` (which reaches back into the store via `getImsEndpoint`).
 */

const { getApiUrl } = urls.create(envs.getAppEnv);
const { default: getEnvironment, ENVIRONMENTS } = environments;

export type CredentialTokenType = 'access_token' | 'authorization_code';

export type OriginValidation = {
  client_id: string;
  relay: string;
  asserted_origin: string;
};

export async function validateOriginWithCredential(
  { client_id, relay, asserted_origin }: OriginValidation,
  tokenType: CredentialTokenType,
  token: string
): Promise<boolean> {
  // Local dev has no real registered redirect URI to validate against, so the
  // real network call can only fail (CORS). Mirror the IS_LOCAL short-circuit
  // used by wrapper-popup-helper/index.ts and app-elm/wrapper/iframe/index.html.
  if (getEnvironment(envs.getAppEnv).isEnvironment(ENVIRONMENTS.local)) {
    return true;
  }

  // Everything below can only fail closed: any error — v2 unreachable, a
  // malformed response, or an unexpected throw while building the request — is
  // logged as `validate-origin-error` and returns false. The gate never throws,
  // so every outcome is observable in Splunk (released / rejected / error) and
  // fire-and-forget callers can't produce an unhandled rejection.
  // Captured for the error log below: undefined if the fetch never got a
  // response (network / CORS / abort -> "Failed to fetch"), the HTTP status if
  // IMS responded but we couldn't use it -- the split that says whether the
  // call even reached IMS.
  let responseStatus: number | undefined;
  try {
    // No credential means nothing to authenticate the v2 call with (and nothing
    // to release) — fail closed rather than issue an unauthenticated request.
    if (!token) {
      createLogger(envs.getAppEnv, client_id, relay, 'validate-origin-error')({
        error: {
          name: 'no credential',
          message: 'empty bearer; failing closed',
        },
      });
      return false;
    }

    const endpoint = new URL('/ims/validate_client/v2', getApiUrl());
    endpoint.searchParams.append('client_id', client_id);
    endpoint.searchParams.append('redirect_uri', asserted_origin);
    endpoint.searchParams.append('relay', relay);
    endpoint.searchParams.append('token_type', tokenType);

    const response = await fetch(endpoint.toString(), {
      headers: { Authorization: `Bearer ${token}` },
    });
    responseStatus = response.status;
    const body = await response.json();
    const valid = body?.valid === true;

    // Rollout observability: log EVERY gate decision as a distinct per-client
    // signal so success vs failure is timechartable, not just failures:
    //   f.name="validate-origin-released" → valid   (credential released)
    //   f.name="validate-origin-rejected" → invalid (withheld → on-auth-failed)
    // asserted_origin/client_id are not secrets; never log `token`.
    createLogger(
      envs.getAppEnv,
      client_id,
      relay,
      valid ? 'validate-origin-released' : 'validate-origin-rejected'
    )({
      info: valid
        ? 'origin validated; releasing credential'
        : 'origin rejected by validate_client/v2',
      asserted_origin,
      token_type: tokenType,
      status: response.status,
    });

    return valid;
  } catch (e) {
    // Fail closed: if we cannot confirm the origin, do not release the
    // credential. Logged so an IMS outage (spikes of on-auth-failed
    // { reason: 'origin-invalid' }) stays diagnosable — the other callers of
    // this endpoint (wrapper-popup-helper, react popup) log on failure too.
    createLogger(envs.getAppEnv, client_id, relay, 'validate-origin-error')({
      error: {
        name: 'validateOriginWithCredential failed',
        message: (e as Error)?.message,
      },
      // status: undefined => the fetch never got a response (network / CORS /
      // abort => "Failed to fetch"); a number => IMS responded but we couldn't
      // use it. That split says whether the call even reached IMS. token_type
      // mirrors the released/rejected logs to aid correlation to the IMS
      // audit/access logs. relay already ships as this log's `c` field (from
      // createLogger above), so it is not repeated in the payload. Never log `token`.
      status: responseStatus,
      token_type: tokenType,
    });
    return false;
  }
}
